import { onlyUnique } from "../../services/util";
import {
  CollectionRef,
  ProductDb,
  getCollectionProductIds,
} from "../products/product";
import { CatalogItemRef } from "./catalogs";

type ProductContainerState = "unset" | "products" | "collection";

export const INDIVIDUAL_PRODUCTS = "0";
export const ALL_PRODUCTS = "all";
export const CATALOG_PRODUCTS = "catalog";
/** productSource written by the Concierge for a grid that carries an explicit product list. */
export const INDIVIDUAL_SOURCE = "individual";

export type CatalogProductSource =
  | "all"
  | "catalog"
  | "individual"
  | "collection"
  | "category";

export type ProductContainerFields = {
  collectionId?: string;
  categoryId?: string;
  products?: string[];
  inStock?: boolean;
  productSource?: CatalogProductSource;
};

export type ProductContainer = {
  collection?: CollectionRef;
  previewProductIds?: string[];
  useSampleProducts?: boolean;
} & ProductContainerFields;

/**
 * True when the container's products are an EXPLICIT list, not a binding to resolve.
 *
 * Such a container may still carry a collectionId/categoryId — the id it was BUILT from.
 * That id is provenance, never the product list: resolving it returns the binding's CURRENT
 * membership and silently discards the edits. Server-side normalisation
 * (GridGenerator.BindingFor) stops new ones being written; this keeps existing rows honest.
 */
export const isExplicitProductList = (container: ProductContainer): boolean =>
  container?.productSource === INDIVIDUAL_SOURCE;

/**
 * Does this container resolve its products from a BINDING (collection / category / all products)
 * at render time, or does it carry an explicit list that is already written out as real pages?
 *
 * Every surface that answers "what is in this grid" forks here. Ranking the binding above the
 * list is what made the designer render a 22-product grid three times over, once per source
 * page, each time resolving the source category's 24.
 */
export const isBoundContainer = (container: ProductContainer): boolean =>
  !isExplicitProductList(container) &&
  (!!container?.collection ||
    container?.productSource === ALL_PRODUCTS ||
    container?.productSource === CATALOG_PRODUCTS ||
    !!container?.categoryId);

export const getProductContainerState = (
  container: ProductContainer
): ProductContainerState => {
  if (!container) return "unset";

  // An explicit list outranks any binding left over on the container. Past that, fall through
  // to exactly the answer an individual container has always got, empty list included.
  if (isExplicitProductList(container))
    return container.products?.length ||
      container.collectionId === INDIVIDUAL_PRODUCTS
      ? "products"
      : "unset";

  return (container.collectionId &&
    container.collectionId !== INDIVIDUAL_PRODUCTS) ||
    container.productSource === CATALOG_PRODUCTS ||
    container.productSource === ALL_PRODUCTS ||
    container.categoryId
    ? "collection"
    : container.products?.length ||
      container.collectionId === INDIVIDUAL_PRODUCTS
    ? "products"
    : "unset";
};

export const isEmptyContainer = (container: ProductContainer): boolean => {
  if (!container) return true;

  const hasProducts =
    (container.collectionId &&
      container.collectionId !== INDIVIDUAL_PRODUCTS) ||
    container.productSource === CATALOG_PRODUCTS ||
    container.productSource === ALL_PRODUCTS ||
    container.categoryId ||
    container.products?.length > 0;

  return !hasProducts;
};

export const initProductContainerCollection = (
  productDb: ProductDb,
  container: ProductContainer,
  items?: CatalogItemRef[]
) => {
  const explicit = isExplicitProductList(container);

  if (
    !explicit &&
    container.collectionId &&
    container.collectionId !== INDIVIDUAL_PRODUCTS &&
    container.collectionId !== ALL_PRODUCTS
  ) {
    container.collection = productDb.collectionMap.get(container.collectionId);
  } else {
    container.collection = null;
  }

  container.previewProductIds = container.products ?? [];

  // An explicit list IS the effective list — resolve no binding over the top of it.
  if (explicit) {
    return container;
  }

  if (
    container.collectionId === ALL_PRODUCTS ||
    container.productSource === ALL_PRODUCTS
  ) {
    container.previewProductIds = Array.from(productDb.productMap.values())
      .sortList((p) => p.product.rank)
      .sortList((p) => p.product.name)
      .map((p) => p.id);
  } else if (container.productSource === CATALOG_PRODUCTS) {
    container.previewProductIds =
      items
        ?.filter((i) => i.di.ItemId)
        .map((i) => i.di.ItemId)
        .filter(onlyUnique) ?? [];
  } else if (container.categoryId) {
    container.previewProductIds = Array.from(productDb.productMap.values())
      .filter((p) => p.category.group.id === container.categoryId)
      .sortList((p) => p.product.rank)
      .sortList((p) => p.product.name)
      .map((p) => p.id);
  } else if (container.collection) {
    container.previewProductIds = getCollectionProductIds(
      productDb,
      container.collection,
      container.inStock
    );
  }
  return container;
};

export const getContainerFields = (
  container: ProductContainer
): ProductContainerFields => {
  const { collectionId, categoryId, products, inStock, productSource } = container;
  const result: ProductContainerFields = {
    collectionId,
    categoryId,
    products,
    inStock,
    productSource,
  };
  if (container.collectionId === INDIVIDUAL_PRODUCTS) {
    result.collectionId = null;
    result.productSource = null;
  }
  if (
    container.collectionId === ALL_PRODUCTS ||
    container.productSource === ALL_PRODUCTS
  ) {
    result.collectionId = null;
    result.productSource = ALL_PRODUCTS;
  }
  return result;
};
